import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Input } from "../../common/components/Input";
import { spacing } from "../../styles/theme";
import {
  SpecsAssetImage,
  SpecsInputField,
  SpecsSelectField,
} from "./SpecsEditorFields/index";
import { getAssetPreviewUrl } from "./specsEditorUtils";
import { formatSpecsInheritedPlaceholder } from "./specsInheritedPlaceholder";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useProductVersionMetadataModel } from "./useProductVersionMetadataModel";
import PartPickerLeftMenu from "./PartPickerLeftMenu";

export default function SpecsElementsPage() {
  const {
    currentVersion,
    editor,
    modals,
    addElement,
    updateElement,
    removeElement,
    setActiveStyle,
  } = useSpecsEditorContextValue();
  const { identity, rawVersionMetadata, updateRawVersionMetadata } = editor;
  const parts = currentVersion?.parts ?? [];
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [newElementName, setNewElementName] = React.useState("");
  const [newElementPartName, setNewElementPartName] = React.useState("");
  const [selectedPartName, setSelectedPartName] = React.useState("");
  const metadataModel = useProductVersionMetadataModel({
    rawVersionMetadata,
    updateRawVersionMetadata,
  });
  const elementStudioLabelKeys = metadataModel.studioLabelKeys?.elements ?? {};

  const setElementStudioLabelKey = (elementName: string, value: string) => {
    const nextElements = { ...elementStudioLabelKeys };
    if (value.trim()) {
      nextElements[elementName] = value;
    } else {
      delete nextElements[elementName];
    }
    metadataModel.studioLabelKeys!.elements = nextElements;
  };
  const selectedPart =
    parts.find((part) => part.name === selectedPartName) ?? parts[0] ?? null;
  const selectedPartElements = selectedPart?.elements ?? [];

  React.useEffect(() => {
    if (!selectedPart) return;
    if (selectedPartName === selectedPart.name) return;
    setSelectedPartName(selectedPart.name);
  }, [selectedPart, selectedPartName]);

  const openAddDialog = React.useCallback(() => {
    setNewElementName("");
    setNewElementPartName(selectedPart?.name || parts[0]?.name || "");
    setAddDialogOpen(true);
  }, [parts, selectedPart]);
  const closeAddDialog = React.useCallback(() => {
    setAddDialogOpen(false);
  }, []);
  const handleAddElement = React.useCallback(() => {
    const elementName = newElementName.trim();
    if (!elementName || !newElementPartName) {
      return;
    }

    addElement(elementName, newElementPartName);
    editor.selectElement(newElementPartName, elementName);
    setAddDialogOpen(false);
  }, [addElement, editor, newElementName, newElementPartName]);

  return (
    <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h2">Éléments</Typography>
      </Stack>

      {parts.length === 0 || !selectedPart ? (
        <Alert severity="info">
          Add parts before configuring element metadata.
        </Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "240px minmax(0, 1fr)" },
            gap: 3,
          }}
        >
          <PartPickerLeftMenu
            parts={parts}
            selectedPartName={selectedPart.name}
            onSelectPart={setSelectedPartName}
          />

          {selectedPartElements.length === 0 ? (
            <Alert severity="info">No elements assigned to this part.</Alert>
          ) : (
            <Stack spacing={2.5}>
              {selectedPartElements.map((element) => {
                const elementKey = `${selectedPart.name}:${element.name}`;
                const debugPath = element._debugPath;
                const placeholder =
                  element.name === selectedPart.mainElement &&
                  identity.productName
                    ? `specs.${identity.productName}.${selectedPart.name}`
                    : `specs.${identity.productName}.${element.name}`;

                return (
                  <Box
                    key={elementKey}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(180px, 0.8fr) minmax(0, 1fr)",
                      },
                      gap: 2,
                      pb: 2.5,
                      borderBottom: "1px solid rgba(0,0,0,0.08)",
                      "&:last-of-type": { borderBottom: "none", pb: 0 },
                    }}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          {element.name}
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() =>
                            modals.openPromptDialog(
                              "Rename Element",
                              "New name",
                              element.name,
                              (newName) => {
                                if (newName !== element.name) {
                                  updateElement(
                                    element.name,
                                    { name: newName },
                                    selectedPart.name
                                  );
                                }
                              }
                            )
                          }
                          sx={{ minWidth: 0, px: 0.75 }}
                        >
                          Renommer
                        </Button>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {selectedPart.name}
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() =>
                          removeElement(element.name, selectedPart.name)
                        }
                        sx={{ mt: 0.5, minWidth: 0, px: 0 }}
                      >
                        Supprimer
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                        alignItems: "start",
                      }}
                    >
                      <SpecsInputField
                        label="Name"
                        debugPath={debugPath ? `${debugPath}.name` : undefined}
                        value={element.name}
                        disabled
                      />
                      <SpecsInputField
                        label="Part"
                        value={selectedPart.name}
                        disabled
                      />

                      <SpecsSelectField
                        label="Style par défaut"
                        debugPath={
                          debugPath ? `${debugPath}.defaultStyle` : undefined
                        }
                        value={element.defaultStyle}
                        options={element.styles.map((style) => ({
                          value: style.name,
                          label: style.name,
                        }))}
                        onChange={(styleName) =>
                          setActiveStyle(
                            selectedPart.name,
                            element.name,
                            styleName
                          )
                        }
                      />

                      <FormControlLabel
                        sx={{ alignSelf: "end", minHeight: 58 }}
                        control={
                          <Checkbox
                            checked={element.displayable}
                            onChange={(_, displayable) =>
                              updateElement(
                                element.name,
                                { displayable },
                                selectedPart.name
                              )
                            }
                          />
                        }
                        label="Displayable"
                      />

                      <Stack direction="row" spacing={1} alignItems="center">
                        {element.menuIconUrl ? (
                          <SpecsAssetImage
                            src={getAssetPreviewUrl(
                              element.displayExtra,
                              "menuIconUrl",
                              element.menuIconUrl
                            )}
                            alt=""
                            sx={{
                              width: 32,
                              height: 32,
                              objectFit: "contain",
                            }}
                          />
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            modals.openIconPicker({
                              kind: "element",
                              partName: selectedPart.name,
                              elementName: element.name,
                            })
                          }
                        >
                          {element.menuIconUrl
                            ? "Changer l'icône"
                            : "Ajouter une icône"}
                        </Button>
                        {element.menuIconUrl ? (
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            onClick={() =>
                              updateElement(
                                element.name,
                                { menuIconUrl: "" },
                                selectedPart.name
                              )
                            }
                          >
                            Supprimer
                          </Button>
                        ) : null}
                      </Stack>

                      <Input
                        label="Studio label key"
                        debugPath={`window.specs.metadataModel.studioLabelKeys.elements.${element.name}`}
                        noMaxWidth
                        fullWidth
                        value={elementStudioLabelKeys[element.name] || ""}
                        onChange={(e) =>
                          setElementStudioLabelKey(element.name, e.target.value)
                        }
                        placeholder={formatSpecsInheritedPlaceholder(
                          placeholder
                        )}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          size="small"
          variant="contained"
          disabled={parts.length === 0}
          onClick={openAddDialog}
        >
          Add element
        </Button>
      </Stack>

      <Dialog
        open={addDialogOpen}
        onClose={closeAddDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Add Element</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Stack spacing={2}>
            <SpecsSelectField
              label="Part"
              value={newElementPartName}
              options={parts.map((part) => ({
                value: part.name,
                label: part.name,
              }))}
              onChange={setNewElementPartName}
            />
            <SpecsInputField
              autoFocus
              label="Element name"
              inputProps={{ "aria-label": "Element name" }}
              value={newElementName}
              placeholder="pocket, collar"
              onChange={(event) => setNewElementName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleAddElement();
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAddDialog} size="small">
            Annuler
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!newElementName.trim() || !newElementPartName}
            onClick={handleAddElement}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
